import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Pencil,
  MoreHorizontal,
  Trash2,
  Truck,
  Plus,
  BriefcaseBusiness,
} from "lucide-react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import profileIcon from "../../../assets/profile_icon.svg";
import AddPayment from "./AddPayment";
import AddWorkOrder from "./AddWorkOrder";
import IndividualWorkOrder from "./IndividualWorkOrder";
import { WorkOrderApi } from "../../../api/workorders/service";
import { ContractorApi } from "../../../api/contractors/service";
import ContractorUpdate from "./ContractorUpdate";
import { PaymentDetailModal } from "./ContractorPaymentModal";
import { WorkOrderInvoiceDocument } from "./WorkOrderInvoicePDF";
import { useAlert } from "components/UI_components/Alert";

export const convertToStringIndex = (num) => {
  return String(num).padStart(3, "0");
};

const ContractorDetails = ({ contractor, onBack, onContractorDeleted }) => {
  const { addAlert } = useAlert();
  const [activeTab, setActiveTab] = useState("workorders");
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddWorkOrder, setShowAddWorkOrder] = useState(false);
  const [showUpdateContractor, setShowUpdateContractor] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [showIndividualWO, setShowIndividualWO] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [totalContractAmount, setTotalContractAmount] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [lastPaymentDate, setLastPaymentDate] = useState("");
  const [lastPaymentAmount, setLastPaymentAmount] = useState(0);
  const [reloadWorkOrdersKey, setReloadWorkOrdersKey] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isDeletingContractor, setIsDeletingContractor] = useState(false);
  const [contractorInfo, setContractorInfo] = useState({
    name: contractor?.name || "",
    contact: contractor?.contact || "",
    address: contractor?.address || "",
  });
  useEffect(() => {
    setContractorInfo({
      name: contractor?.name || "",
      contact: contractor?.contact || "",
      address: contractor?.address || "",
    });
  }, [contractor?.id]);
  useEffect(() => {
    if (!contractor?.id) return;
    let isCancelled = false;
    const load = async () => {
      try {
        setLoadingWorkOrders(true);
        const res = await WorkOrderApi.listByContractor(contractor.id);
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.results)
            ? res.results
            : Array.isArray(res)
              ? res
              : [];

        const normalized = list.map((item) => ({
          id: String(
            item.id ?? item.code ?? item.work_order_id ?? item.reference ?? "-",
          ),
          site:
            item.site?.name ||
            item.site_name ||
            item.project_name ||
            item.site ||
            "-",
          value: Number(
            item.total_amount ??
              item.value ??
              item.work_order_value ??
              item.agreed_value ??
              0,
          ),
          paid: Number(
            item.total_paid_amount ??
              item.paid ??
              item.amount_paid ??
              item.total_paid ??
              0,
          ),
          date:
            item.date_of_issue ||
            item.issue_date ||
            item.date ||
            item.created_at ||
            "",
        }));
        const totalContract = normalized.reduce(
          (sum, item) => sum + item.value,
          0,
        );
        setTotalContractAmount(totalContract);
        if (!isCancelled) setWorkOrders(normalized);
      } catch (e) {
        if (!isCancelled) setWorkOrders([]);
      } finally {
        if (!isCancelled) setLoadingWorkOrders(false);
      }
    };
    load();
    return () => {
      isCancelled = true;
    };
  }, [contractor?.id, reloadWorkOrdersKey]);

  // const payments = [
  //   {
  //     id: "Contractorpay_01",
  //     site: "Angshuman Residency",
  //     amount: 9600,
  //     method: "Cheque",
  //     paidBy: "Supratik Rakshit",
  //   },
  //   {
  //     id: "Contractorpay_02",
  //     site: "Plutone Real Estate",
  //     amount: 1500,
  //     method: "UPI",
  //     paidBy: "Apratim Dutta",
  //   },
  //   {
  //     id: "Contractorpay_03",
  //     site: "Megh Residency",
  //     amount: 2500,
  //     method: "Cash",
  //     paidBy: "You",
  //   },
  //   {
  //     id: "Contractorpay_04",
  //     site: "Plutone Real Estate",
  //     amount: 4500,
  //     method: "Cash",
  //     paidBy: "Siteish Dash",
  //   },
  //   {
  //     id: "Contractorpay_05",
  //     site: "Prakash complex",
  //     amount: 2500,
  //     method: "Cash",
  //     paidBy: "Harshal Mehta",
  //   },
  // ];

  // work orders are loaded from API

  const [payments, setPayments] = useState([]);

  const loadPayments = () => {
    if (!contractor?.id) return;
    try {
      (async () => {
        const data = await WorkOrderApi.getPaymentHistory(contractor.id);
        const list = data?.data;
        const normalized = Array.isArray(list)
          ? list.map((item) => ({
              id: item.id,
              site: item.workorder_site || "-",
              amount: Number(item.paid_amount ?? 0),
              workorder_id: item.workorder_id,
              payment_date: item.payment_date,
              updated_on: item.updated_on,
              payment_type: item.payment_type || "-",
              method: item.payment_method || "-",
              paidBy: item.updated_by || "-",
            }))
          : [];
        const total = normalized.reduce((sum, item) => sum + item.amount, 0);
        const lastPayment = [...normalized].sort(
          (a, b) => new Date(b.payment_date) - new Date(a.payment_date),
        )[0];
        const lastDate = lastPayment
          ? new Date(lastPayment.updated_on).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";
        const lastAmount = lastPayment ? lastPayment.amount : 0;
        setTotalPayment(total);
        setLastPaymentDate(lastDate);
        setLastPaymentAmount(lastAmount);
        // console.log("Loaded payments:", normalized);
        setPayments(normalized);
      })();
    } catch (e) {
      setPayments([]);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [contractor?.id]);

  const handleDeleteContractor = async () => {
    if (!contractor?.id) return;
    addAlert(
      "Are you sure you want to delete this contractor? This action cannot be undone.",
      "confirm",
      {
        onConfirm: async () => {
          try {
            setIsDeletingContractor(true);
            await ContractorApi.deleteById(contractor.id);
            addAlert("Contractor deleted successfully", "success");
            if (typeof onContractorDeleted === "function") {
              onContractorDeleted();
            }
            onBack?.();
          } catch (error) {
            console.error("Error deleting contractor:", error);
            addAlert(error?.message || "Error deleting contractor", "error");
          } finally {
            setIsDeletingContractor(false);
          }
        },
      },
    );
  };
  return (
    <>
      {showAddPayment && (
        <AddPayment
          contractor={contractor}
          onClose={() => {
            setShowAddPayment(false);
            loadPayments();
          }}
        />
      )}

      {showAddWorkOrder && (
        <AddWorkOrder
          contractor={contractor}
          onClose={() => {
            setShowAddWorkOrder(false);
            setReloadWorkOrdersKey((k) => k + 1);
          }}
        />
      )}

      {showIndividualWO && (
        <IndividualWorkOrder
          contractor={contractor}
          workOrder={selectedWorkOrder}
          onClose={() => setShowIndividualWO(false)}
        />
      )}

      {showUpdateContractor && (
        <ContractorUpdate
          contractor={contractor}
          onClose={() => setShowUpdateContractor(false)}
          onUpdated={(updated) => {
            setContractorInfo((prev) => ({
              ...prev,
              ...updated,
            }));
          }}
        />
      )}

      {selectedPayment && (
        <PaymentDetailModal
          payment={selectedPayment}
          contractor={contractor}
          onClose={() => setSelectedPayment(null)}
        />
      )}

      {!showAddPayment && !showAddWorkOrder && !showIndividualWO && (
        <div
          className={`text-black w-[85%] px-4 mx-auto py-3 mt-2 overflow-hidden flex flex-col gap-3 relative ${
            showAddPayment ? "opacity-30 pointer-events-none" : ""
          }`}
        >
          <div
            className={`flex flex-col bg-white rounded-xl z-10 px-3 pt-3 border-1 border-[#A3A3A3]`}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex gap-2 items-start">
                <ArrowLeft
                  className="mt-1 cursor-pointer text-[#232323]"
                  onClick={onBack}
                />
                <img
                  src={profileIcon}
                  className="h-10 w-10"
                  alt="Profile Icon"
                />
                <div>
                  <h2 className="font-medium text-[16px] text-[#232323]">
                    {contractorInfo.name || contractor.name}
                  </h2>
                  <p className="text-[14px] font-light text-[#232323]">
                    {contractor.contractor_type}
                  </p>
                </div>
                <span className="ml-1 text-[12px] px-2 py-0.5 rounded-full bg-[#E3E3E3] text-[#737373] font-normal">
                  Contractor
                </span>
              </div>

              <div className="text-left space-y-1">
                {/* <p className="text-sm">
                  In Charge :{" "}
                  <span className="text-[#232323] text-[14px] font-medium">
                    {contractor.name}
                  </span>
                </p> */}

                <p className="text-[14px] text-[#232323] font-normal">
                  Contact No :
                  <span className="text-[#232323] text-[14px] font-medium">
                    {contractorInfo.contact || contractor.contact || "N/A"}
                  </span>
                </p>
                <p className="text-[14px] text-[#232323] font-normal">
                  Address :{" "}
                  <span className="text-[14px] text-[#232323] font-normal ml-2">
                    {contractorInfo.address || contractor.address || "N/A"}
                  </span>
                </p>
              </div>
              <div className="flex gap-[30px] mt-1">
                <button
                  className="text-[#1C1B1F] hover:text-black disabled:opacity-50"
                  onClick={() => setShowUpdateContractor(true)}
                  disabled={isDeletingContractor}
                  title="Edit contractor"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  className="text-red-600 hover:text-red-800 disabled:opacity-50"
                  onClick={handleDeleteContractor}
                  disabled={isDeletingContractor}
                  title="Delete contractor"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            <hr className="border-1 mb-3 w-full border-[#232323]" />
            <div>
              <p className="text-[18px] font-normal text-[#232323] pb-2 ml-5 pt-1 ">
                Payment Info
              </p>
              <div className="flex gap-4 flex-wrap items-stretch min-h-[48px] mb-4 mt-2">
                <div className="flex items-center justify-between gap-4 border-1 border-[#CCCCCC] rounded-xl px-4 py-0.5 grow-0 basis-[30%] min-w-[200px]">
                  <p className="text-[16px] font-light text-[#232323]">
                    Payment till now
                  </p>
                  <p className="text-[16px] font-medium text-[#232323]">
                    ₹{totalPayment.toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-4 border-1 border-[#CCCCCC] rounded-xl px-4 py-0.5 grow-0 basis-[30%] min-w-[200px] ">
                  <p className="text-[16px] font-light text-[#232323]">
                    Remaining Payment
                  </p>
                  <p className="text-[16px] font-medium text-[#232323]">
                    ₹{(totalContractAmount - totalPayment).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-4 border-1 border-[#CCCCCC] rounded-xl px-4 py-0.5 grow-0 basis-[30%] min-w-[200px] ">
                  <p className="text-[16px] font-light text-[#232323]">
                    Last Payment
                  </p>
                  <div className="text-right">
                    <p className="text-[16px] font-medium text-[#232323]">
                      ₹{lastPaymentAmount.toLocaleString()}
                    </p>
                    <p className="text-[12px] font-light text-[#232323]">
                      {lastPaymentDate}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <hr className="border-1 mt-2 mb-4 w-full border-[#232323]" />

            <div className="flex gap-10 w-full mb-0 mt-1">
              <div
                className={`flex items-center justify-center w-1/2 gap-2 pb-3 cursor-pointer ${
                  activeTab === "workorders"
                    ? "border-b-[3px] border-[#000000BF]"
                    : "text-[#B2B2B2]"
                }`}
                onClick={() => setActiveTab("workorders")}
              >
                <BriefcaseBusiness />
                <span
                  className={
                    activeTab === "workorders"
                      ? "text-[#1C1B1F] font-semibold text-[16px]"
                      : "text-[#737373] font-normal text-[16px]"
                  }
                >
                  Work Order
                </span>
              </div>
              <div
                className={`flex items-center justify-center w-1/2 gap-2 pb-2 cursor-pointer ${
                  activeTab === "payments"
                    ? "border-b-[3px] border-[#000000BF]"
                    : "text-[#B2B2B2]"
                }`}
                onClick={() => setActiveTab("payments")}
              >
                <Truck className="w-[23px]" />
                <span
                  className={
                    activeTab === "payments"
                      ? "text-[#1C1B1F] font-semibold text-[16px]"
                      : "text-[#737373] font-normal text-[16px]"
                  }
                >
                  Payments
                </span>
              </div>
            </div>

            {/* <div className="flex justify-between items-center"></div> */}
          </div>

          {/* Filters and action button outside the white rectangle (grey area) */}

          <div className="flex justify-between px-3 items-center">
            {activeTab === "payments" ? (
              <div className="bg-[#FFFFFF] w-fit px-3 py-1 text-[14px] rounded-md text-[#877A90]">
                Today - Last 7 days
              </div>
            ) : (
              <p className="text-sm text-[#6B6B6B] font-medium">
                {workOrders.length} Work Orders
              </p>
            )}
            <button
              className="bg-[#3B0069] w-[180px] flex items-center justify-center text-white font-medium px-4 py-2 rounded-lg gap-2"
              onClick={() => {
                if (activeTab === "payments") setShowAddPayment(true);
                if (activeTab === "workorders") setShowAddWorkOrder(true);
              }}
            >
              <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
                <Plus className="w-4 h-4 text-white" />
              </div>
              {activeTab === "payments" ? "Payment" : "Work Order"}
            </button>
          </div>

          <div className={`flex flex-col px-2 pb-4 overflow-y-auto space-y-3 `}>
            {activeTab === "payments"
              ? payments.map((pay, idx) => (
                  <div
                    key={idx}
                    style={{
                      transition: "transform 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.01)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    onClick={() => {
                      setSelectedPayment(pay);
                    }}
                    className="flex justify-between cursor-pointer items-center bg-white hover:bg-gray-100 py-2 px-3 border-[0.5px] border-[#C3C3C3] rounded-xl shadow-sm"
                  >
                    <div className="font-semibold text-[#232323] text-[14px] w-[160px]">
                      #ContractorPay_WO_
                      {convertToStringIndex(pay?.workorder_id)}
                    </div>
                    <div className="bg-[#ECE6F0] text-[#3B0069] px-2 py-1 text-[11px] rounded-full font-medium w-[160px] text-center">
                      {pay.site}
                    </div>
                    <div className="font-semibold text-[16px] text-[#232323] w-[160px] text-center">
                      ₹{pay.amount}
                      <span className="ml-2 text-[12px] font-normal text-[#58267F] bg-[#ECE6F0] border rounded-full px-2 py-0.5">
                        {pay.method}
                      </span>
                    </div>
                    <div className="flex flex-row items-center gap-2 w-[180px]">
                      <span className="text-sm text-[#232323]">
                        {new Date(pay.payment_date).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                        ,{" "}
                        {new Date(pay.updated_on).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <div className="bg-[#F5F5F5] text-[#737373] font-medium text-xs rounded-xl px-2 ">
                        {pay.payment_type}
                      </div>
                    </div>
                    <div className="text-left font-normal text-[#737373] text-[12px] w-[160px]">
                      Paid by
                      <br />
                      <span className="text-[14px] font-medium text-[#232323]">
                        {pay.paidBy}
                      </span>
                    </div>
                  </div>
                ))
              : workOrders.map((wo, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center py-2 px-3 border rounded-lg bg-white shadow-sm cursor-pointer"
                    onClick={() => {
                      setSelectedWorkOrder(wo);
                      setShowIndividualWO(true);
                    }}
                    // on hover scale up
                    style={{
                      transition: "transform 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.01)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <div className="font-semibold text-[#232323] text-[14px] w-[220px]">
                      #WO_{wo?.site}_{convertToStringIndex(wo.id)}
                    </div>
                    <div className="text-[12px] text-[#737373] w-[160px]">
                      <div>Work Order Value</div>
                      <div className="font-medium text-[14px] text-[#232323]">
                        ₹{wo.value.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-[12px] text-[#737373] w-[160px]">
                      <div>Amount Paid</div>
                      <div className="font-medium text-[14px] text-[#232323]">
                        ₹{wo.paid.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-[12px] text-[#737373] w-[160px]">
                      <div>Date of Issue</div>
                      <div className="font-medium text-[14px] text-[#232323]">
                        {wo.date}
                      </div>
                    </div>
                    <div className="w-[240px] flex items-center justify-center gap-3">
                      <span className="bg-[#ECE6F0] text-[11px] text-[#3B0069] px-3 py-1 text[#3B0069] rounded-full font-medium">
                        {wo.site}
                      </span>
                      <PDFDownloadLink
                        document={
                          <WorkOrderInvoiceDocument
                            contractor={contractor}
                            workOrder={wo}
                          />
                        }
                        fileName={`WorkOrder_Invoice_WO_${convertToStringIndex(
                          wo.id,
                        )}.pdf`}
                        onClick={(e) => {
                          // Prevent opening the Individual Work Order view
                          // before the PDF link processes the click
                          e.stopPropagation();
                        }}
                      >
                        {({ loading }) => (
                          <button
                            className="bg-[#3B0069] text-white text-xs px-3 py-1.5 rounded-md hover:bg-[#2d0051]"
                            disabled={loading}
                            onClick={(e) => {
                              // Also stop propagation from the inner button
                              e.stopPropagation();
                            }}
                          >
                            {loading ? "Preparing..." : "Download"}
                          </button>
                        )}
                      </PDFDownloadLink>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ContractorDetails;
